import React from 'react';

import Text from 'admin/components/common/Text';
import Button from 'admin/components/common/Button';
import ParamsTable from 'admin/components/common/Intregrations/ParamsTable';
import { TextField } from 'admin/components/common/Form';
import { Icon } from 'admin/components/common/Icon';
import { Modal, ModalBody, ModalFooter } from 'admin/components/common/Modal';
import { ModalManagerProvidedProps } from 'admin/components/common/ModalManager';

import { MODAL } from 'admin/constants/common';
import cms from 'common/utils/cms';

import { parseAdUnitTargetingParams } from 'utils/ad-unit-targeting';
import t from 'utils/translate';

import type { AdUnitTargetingParams } from '../types';

import css from './Modal.scss';

const modalText = (path: string): string => t(`story.adUnitTargetingParamsModal.${path}`);

export type AdUnitTargetingParamsModalData = {
	title?: string;
	params?: string;
	onSubmit: (params: AdUnitTargetingParams) => unknown;
	onClear: () => unknown;
};

type Props = ModalManagerProvidedProps<MODAL.AD_UNIT_TARGETING_PARAMS>;

const AdUnitTargetingParamsModal = ({ open, data, ...props }: Props) => {
	const { title, params: adTargetingParams } = data;
	const theme = 'dark';
	const [parsedParams] = React.useState(() => parseAdUnitTargetingParams(adTargetingParams ?? ''));
	const [paramName, setParamName] = React.useState('');
	const [paramValue, setParamValue] = React.useState('');
	const [error, setError] = React.useState<{ name: string; value: string }>({
		name: '',
		value: '',
	});
	const [params, setParams] = React.useState(parsedParams);

	const onParamNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.currentTarget.value;

		if (/(\s)/g.test(val)) {
			setError({ ...error, name: modalText('errors.invalid') });
		} else if (params.find(p => p.name === val)) {
			setError({ ...error, name: modalText('errors.alreadyExist') });
		} else if (error.name) {
			setError({ ...error, name: '' });
		}

		setParamName(e.currentTarget.value);
	};

	const onParamValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setParamValue(e.currentTarget.value);
	};

	const onAddParamClick = () => {
		setParams(p => [...p, { name: paramName, value: paramValue }]);
		setParamName('');
		setParamValue('');
	};

	const onDeleteParamBtnClick = (targetIndex: number) => {
		setParams(p => p.filter((_, i) => i !== targetIndex));
	};

	const onCancel = () => {
		props.close();
	};

	const onClear = async () => {
		await data.onClear();
		props.close();
	};

	const onSubmit = async () => {
		await data.onSubmit(params);
		props.close();
	};

	const hasChanges = React.useMemo(() => {
		const paramsChanged =
			params.length !== parsedParams.length ||
			params.some((p, i) => {
				const originalParam = parsedParams[i];
				return !originalParam || p.name !== originalParam.name || p.value !== originalParam.value;
			});
		return paramsChanged;
	}, [params, parsedParams]);

	const renderParamsTable = () => {
		const readableParams = params.map(p => {
			const cmsRef = cms.parseReference(p.value, false);
			return {
				name: p.name,
				value: cmsRef ? `$CMS.${cmsRef.dataKey}` : p.value,
			};
		});

		return (
			<ParamsTable
				title={modalText('tableTitle')}
				theme={theme}
				columns={[
					{ label: modalText('colNameTitle'), fieldKey: 'name' },
					{ label: modalText('colValueTitle'), fieldKey: 'value' },
					{ label: ' ', fieldKey: 'action' },
				]}
				data={readableParams.map((p, i) => ({
					...p,
					action: (
						<Button
							view="empty"
							color="danger-ds"
							shape="circle"
							size="tiny"
							onClick={() => onDeleteParamBtnClick(i)}
						>
							<Icon type="minus-rounded" color="currentColor" />
						</Button>
					),
				}))}
			/>
		);
	};

	const renderInputParam = () => {
		return (
			<div className={css.inputParam}>
				<TextField
					name="name"
					placeholder={modalText('fields.paramName.placeholder')}
					isRequired
					value={paramName}
					isLabelUppercase={false}
					error={error.name}
					theme={theme}
					label={modalText('fields.paramName.label')}
					onChange={onParamNameChange}
					autoComplete="off"
				/>
				<TextField
					name="value"
					placeholder={modalText('fields.paramValue.placeholder')}
					isRequired
					value={paramValue}
					isLabelUppercase={false}
					error={error.value}
					theme={theme}
					label={modalText('fields.paramValue.label')}
					onChange={onParamValueChange}
					autoComplete="off"
				/>

				<Button
					view="empty"
					color="primary"
					shape="circle"
					size="small"
					style={{ marginTop: 34 }}
					disabled={!paramName || !paramValue || error.name !== '' || error.value !== ''}
					onClick={onAddParamClick}
				>
					<Icon type="plus-rounded" color="currentColor" />
				</Button>
			</div>
		);
	};

	const renderContent = () => {
		return (
			<>
				<ModalBody>
					{renderInputParam()}
					{renderParamsTable()}
				</ModalBody>
				<ModalFooter>
					<Button view="primary" type="button" onClick={onSubmit} disabled={!hasChanges}>
						{modalText('save')}
					</Button>
					{Boolean(adTargetingParams?.trim()) && (
						<Button view="danger" type="button" onClick={onClear}>
							{modalText('clear')}
						</Button>
					)}
					<Button view="secondary-gray" onClick={onCancel}>
						{modalText('cancel')}
					</Button>
				</ModalFooter>
			</>
		);
	};

	return (
		<Modal
			onCancel={onCancel}
			maskColor="black"
			theme="dark"
			open={open}
			destroyOnClose
			width={parseInt(css.modalWidth, 10)}
			className={css.modal}
			transitionName=""
			title={title ? <Text size={Text.size.subtitle} weight={Text.weight.semibold} compact text={title} /> : null}
		>
			<div>{renderContent()}</div>
		</Modal>
	);
};

export default AdUnitTargetingParamsModal;
